/* ---------------------------------------------------------------------------
 * Element display duration (dwell time)
 * ---------------------------------------------------------------------------
 * How long ONE element stays on an artboard before the display rotates to the
 * next feature/item. Announcements, donations, events and Recent Photos each
 * have their own org-wide value — `organization.announcementDisplayDuration`,
 * `organization.donationDisplayDuration`,
 * `organization.eventDisplayDuration` and
 * `organization.recentPhotosDurationSecs` — stored as a whole number of
 * SECONDS, defaulting to 30 for every organization (the entity column default).
 *
 * Each settings modal enters the value as a number + unit pair (1–60 seconds OR
 * 1–10 minutes); that split is a data-entry convenience only. Everything
 * downstream (Display.vue's carousel, and therefore the Dashboard's embedded
 * selected-screen preview) reads plain seconds through the per-feature getters
 * below, so the two representations can never drift apart.
 *
 * The engine is shared on purpose: the settings modals are meant to look and
 * behave identically. It was ported from the sibling
 * `upwork/screenkode/screenkode-frontend` project, and the announcement /
 * donation halves are kept byte-identical to it.
 */
export type DisplayDurationUnit = 'seconds' | 'minutes';

export const DISPLAY_DURATION_DEFAULT_SECONDS = 30;

/** Widest span a stored value may take: 1 second .. 10 minutes. */
export const DISPLAY_DURATION_MIN_SECONDS = 1;
export const DISPLAY_DURATION_MAX_SECONDS = 600;

export interface DisplayDurationUnitConfig {
  value: DisplayDurationUnit;
  label: string;
  /** Inclusive bounds for the number input while this unit is selected. */
  min: number;
  max: number;
  secondsPerUnit: number;
}

export const DISPLAY_DURATION_UNITS: DisplayDurationUnitConfig[] = [
  { value: 'seconds', label: 'Seconds', min: 1, max: 60, secondsPerUnit: 1 },
  { value: 'minutes', label: 'Minutes', min: 1, max: 10, secondsPerUnit: 60 },
];

export const getDisplayDurationUnitConfig = (
  unit: DisplayDurationUnit
): DisplayDurationUnitConfig =>
  DISPLAY_DURATION_UNITS.find((option) => option.value === unit) || DISPLAY_DURATION_UNITS[0];

/**
 * Coerce any candidate into a valid stored value: a whole number of seconds
 * inside [1, 600]. Anything unusable (null / '' / NaN / 0) falls back to the
 * 30-second default rather than to a boundary, so an unset organization still
 * behaves like the documented default.
 */
export const clampDisplayDurationSeconds = (value: unknown): number => {
  const seconds = Math.round(Number(value));
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return DISPLAY_DURATION_DEFAULT_SECONDS;
  }
  return Math.min(
    Math.max(seconds, DISPLAY_DURATION_MIN_SECONDS),
    DISPLAY_DURATION_MAX_SECONDS
  );
};

/**
 * Split stored seconds into the modal's { amount, unit } pair.
 *
 * Whole minutes are shown as minutes (60 -> "1 Minute", 300 -> "5 Minutes");
 * everything else stays in seconds. A value above 60 that is NOT a whole number
 * of minutes can only arrive from outside this UI (a direct API call), so it is
 * rounded to the nearest representable minute — re-saving from the modal then
 * heals the stored value.
 */
export const toDisplayDurationParts = (
  seconds: unknown
): { amount: number; unit: DisplayDurationUnit } => {
  const total = clampDisplayDurationSeconds(seconds);

  if (total >= 60) {
    const minutesConfig = getDisplayDurationUnitConfig('minutes');
    const minutes = Math.min(Math.max(Math.round(total / 60), minutesConfig.min), minutesConfig.max);
    return { amount: minutes, unit: 'minutes' };
  }

  return { amount: total, unit: 'seconds' };
};

/**
 * Recombine the modal's { amount, unit } pair back into stored seconds, with
 * the amount clamped into the selected unit's own range first.
 */
export const fromDisplayDurationParts = (
  amount: unknown,
  unit: DisplayDurationUnit
): number => {
  const config = getDisplayDurationUnitConfig(unit);
  const rounded = Math.round(Number(amount));
  const safeAmount = Number.isFinite(rounded)
    ? Math.min(Math.max(rounded, config.min), config.max)
    : config.min;
  return clampDisplayDurationSeconds(safeAmount * config.secondsPerUnit);
};

/**
 * Convert an amount when the user flips the unit dropdown, keeping the duration
 * as close as the new unit allows (30 seconds -> 1 minute, 5 minutes -> 60
 * seconds, the closest each unit can express).
 */
export const convertDisplayDurationAmount = (
  amount: unknown,
  fromUnit: DisplayDurationUnit,
  toUnit: DisplayDurationUnit
): number => {
  const toConfig = getDisplayDurationUnitConfig(toUnit);
  if (fromUnit === toUnit) {
    const rounded = Math.round(Number(amount));
    return Number.isFinite(rounded)
      ? Math.min(Math.max(rounded, toConfig.min), toConfig.max)
      : toConfig.min;
  }

  const fromConfig = getDisplayDurationUnitConfig(fromUnit);
  const totalSeconds = Math.round(Number(amount)) * fromConfig.secondsPerUnit;
  if (!Number.isFinite(totalSeconds)) return toConfig.min;

  const converted = Math.round(totalSeconds / toConfig.secondsPerUnit);
  return Math.min(Math.max(converted, toConfig.min), toConfig.max);
};

/** Human-readable summary, e.g. "30 seconds" / "1 minute" / "5 minutes". */
export const formatDisplayDuration = (seconds: unknown): string => {
  const { amount, unit } = toDisplayDurationParts(seconds);
  const noun = unit === 'minutes' ? 'minute' : 'second';
  return `${amount} ${noun}${amount === 1 ? '' : 's'}`;
};

/* --------------------------- per-feature values --------------------------- */

/** The organization's announcement dwell time in seconds, defaulted + clamped. */
export const getAnnouncementDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.announcementDisplayDuration);

/** The organization's announcement dwell time in milliseconds (what a timer wants). */
export const getAnnouncementDisplayDurationMs = (organization: any): number =>
  getAnnouncementDisplayDurationSeconds(organization) * 1000;

/** The organization's donation dwell time in seconds, defaulted + clamped. */
export const getDonationDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.donationDisplayDuration);

/** The organization's donation dwell time in milliseconds (what a timer wants). */
export const getDonationDisplayDurationMs = (organization: any): number =>
  getDonationDisplayDurationSeconds(organization) * 1000;

/**
 * The organization's event dwell time in seconds, defaulted + clamped.
 *
 * One value covers both event kinds: a full Event and a Quick Event are the
 * same record with a different `subType`, and both render through an Event
 * Template, so the board holds either for the same time.
 */
export const getEventDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.eventDisplayDuration);

/** The organization's event dwell time in milliseconds (what a timer wants). */
export const getEventDisplayDurationMs = (organization: any): number =>
  getEventDisplayDurationSeconds(organization) * 1000;

/**
 * How long ONE row of yahrzeits holds the screen, in seconds.
 *
 * Three-step fallback, because this setting arrived after boards were already
 * running: the value the Yurtzeiten settings modal writes wins; failing that the
 * legacy `yurtzeitenSwipeDelay` from the old settings page is honoured, so a
 * board configured before the modal existed keeps its pacing; and with neither
 * given the board holds a row for the artboard's usual minute rather than the
 * 30 seconds announcements and donations default to.
 */
export const YURTZEITEN_DISPLAY_DURATION_FALLBACK_SECONDS = 60;

export const getYurtzeitenDisplayDurationSeconds = (organization: any): number => {
  const configured = Number(organization?.yurtzeitenDisplayDuration);
  if (Number.isFinite(configured) && configured > 0) {
    return clampDisplayDurationSeconds(configured);
  }

  const legacySwipeDelay = Number(organization?.yurtzeitenSwipeDelay);
  if (Number.isFinite(legacySwipeDelay) && legacySwipeDelay > 0) {
    return clampDisplayDurationSeconds(legacySwipeDelay);
  }

  return YURTZEITEN_DISPLAY_DURATION_FALLBACK_SECONDS;
};

/** The same value in milliseconds (what a timer wants). */
export const getYurtzeitenDisplayDurationMs = (organization: any): number =>
  getYurtzeitenDisplayDurationSeconds(organization) * 1000;

/** The organization's Recent Photos dwell time in seconds, defaulted + clamped. */
export const getRecentPhotoDisplayDurationSeconds = (organization: any): number =>
  clampDisplayDurationSeconds(organization?.recentPhotosDurationSecs);

export const getRecentPhotoDisplayDurationMs = (organization: any): number =>
  getRecentPhotoDisplayDurationSeconds(organization) * 1000;
